"use client";

import { JSX, useEffect, useRef } from "react";
import { type Translate, useViewerLanguage } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { scrollIntoViewX } from "@/lib/scroll-into-view-x";
import { SvgFadeMask } from "@/components/charts/svg-fade-mask";
import type { ProcessStatus } from "@/components/charts/process-status";
import {
  useHoverSelect,
  useLegendHoverSelect,
  useSelectionGroup,
} from "@/lib/hooks/use-hover-select";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { NodeTooltip } from "@/components/charts/tooltip/node-tooltip";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
} from "@/components/charts/legend";
import { useSvgContentHeight } from "./shared/use-svg-content-height";
import { useSvgId } from "@/lib/hooks/use-svg-id";
import { useHoverGesture } from "@/lib/hooks/use-hover-gesture";
import {
  buildResolvedLegendItems,
  categoryKeyOf,
  categorySvgKey,
  hasCategoryGrouping,
  legendIndexOfCategory,
  resolveCategories,
  resolveCategoryColor,
} from "./shared/category-resolve";
import type { ModuleCategory } from "@/types/modules";
import { importsTokenScope } from "./imports-token-scope";

export type TimelineStepperStep = {
  id: number | string;
  name: string;
  status?: ProcessStatus;
  categoryId?: string;
  date: string;
  duration?: string;
};

export type TimelineStepperData = {
  steps: TimelineStepperStep[];
  initialSelectedIndex?: number;
  categories?: ModuleCategory[];
};

function legacyTimelineCategories(t: Translate): ModuleCategory[] {
  return [
    { id: "done", label: t("Done"), color: "var(--status-1)" },
    { id: "active", label: t("Active"), color: "var(--status-2)" },
    { id: "pending", label: t("Pending"), color: "var(--status-neutral)" },
  ];
}

const DEFAULT_DATA: TimelineStepperData = {
  steps: [
    { id: 1, name: "Kickoff", status: "done", date: "Sep 01", duration: "1d" },
    { id: 2, name: "Discovery", status: "done", date: "Sep 03", duration: "4d" },
    { id: 3, name: "Design", status: "done", date: "Sep 08", duration: "7d" },
    { id: 4, name: "Build", status: "active", date: "Sep 16", duration: "14d" },
    { id: 5, name: "Review", status: "pending", date: "Sep 30", duration: "3d" },
    { id: 6, name: "Launch", status: "pending", date: "Oct 04", duration: "1d" },
  ],
};

export default function ModuleTimelineStepper({
  data = DEFAULT_DATA,
  showLegend = true,
  grayscale,
  accent,
  className,
}: {
  data?: TimelineStepperData;
  showLegend?: boolean;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t } = useViewerLanguage();
  const steps = data.steps ?? [];
  const freeCategories = data.categories;
  const grouped = hasCategoryGrouping(steps, freeCategories);
  const resolved = resolveCategories(freeCategories, legacyTimelineCategories(t));
  const free = resolved.free;
  const activeStepIdx = steps.findIndex((s) => s.status === "active");
  // Nothing is selected on mount: the first `active` step used to be seeded
  // here, which showed up as a step ring and a preselected legend row before
  // the reader had touched anything.
  const selection = useSelectionGroup();
  const marker = useHoverSelect<number>({
    initialSelected: data.initialSelectedIndex ?? null,
    group: selection,
  });
  const hovered = marker.hovered;
  const selected = marker.selected;
  const legend = useLegendHoverSelect({ group: selection });
  const hoverGesture = useHoverGesture();
  // Every instance needs its own marker ids: the viewer renders this module
  // more than once (measurement pass plus the mounted pages) and a duplicate
  // id makes `url(#…)` resolve to the first copy in the document, which leaves
  // the visible timeline without arrow heads.
  const arrowPrefix = useSvgId("tl-arrow");
  const maskId = useSvgId("tl-mask");
  const arrowSolidId = `${arrowPrefix}-solid`;
  const arrowPendingId = `${arrowPrefix}-pending`;

  const W = 760;
  const H = 200;
  const { svgRef, height: dynamicH } = useSvgContentHeight(H, [steps]);
  const padX = 56;
  const yLine = 100;
  const yTopLbl = 40;
  const yBotLbl = 158;
  const step = (W - padX * 2) / Math.max(1, steps.length - 1);
  const xFor = (i: number) => padX + step * i;
  const dotR = 5;
  const arrowGap = 4;
  const segInset = dotR + arrowGap;

  const progressEnd =
    grouped && !free
      ? activeStepIdx >= 0
        ? activeStepIdx
        : steps.length - 1
      : steps.length - 1;

  const legendItems = buildResolvedLegendItems(steps, resolved);
  const stepKey = (s: TimelineStepperStep) => categoryKeyOf(s, free);
  const legFilter =
    legend.filter !== null ? legendItems[legend.filter]?.id ?? null : null;

  const active = marker.effective;
  // An authored initial selection marks where the timeline stands; only a
  // selection the reader made themselves opens the panel or claims a legend row.
  const pinned = marker.selectedByUser ? marker.selected : null;
  const engaged = marker.hovered ?? pinned;

  const legendSelectedIndex =
    legend.selected ??
    (pinned !== null && steps[pinned]
      ? legendIndexOfCategory(legendItems, stepKey(steps[pinned]))
      : null);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const anchorRefs = useRef<Record<number, SVGGElement | null>>({});
  const activeStep = engaged !== null ? steps[engaged] : null;
  const activeKey = activeStep ? stepKey(activeStep) : undefined;
  const activeStatusMeta = activeKey
    ? legendItems.find((li) => li.id === activeKey)
    : null;
  // Both surfaces feed one scroll target: a legend row centres the first step
  // of its category, a step picked on the timeline is only pulled in when it
  // sits outside the viewport. Keying the effect on the resolved index instead
  // of the rebuilt arrays keeps it from firing on unrelated renders, which is
  // what makes re-picking the same category scroll again.
  const scrollCategory =
    legend.selected !== null ? legendItems[legend.selected]?.id ?? null : null;
  const scrollStepIndex =
    pinned ??
    (scrollCategory
      ? (() => {
          const i = steps.findIndex((s) => stepKey(s) === scrollCategory);
          return i < 0 ? null : i;
        })()
      : null);
  const scrollAlign = pinned !== null ? "nearest" : "center";
  useEffect(() => {
    if (scrollStepIndex === null) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    scrollIntoViewX(
      container,
      container.querySelector(`[data-part-index="${scrollStepIndex}"]`),
      { align: scrollAlign, once: false },
    );
  }, [scrollStepIndex, scrollAlign]);

  return (
    <div className={cn("flex w-full flex-col gap-4", className)} style={importsTokenScope(grayscale, accent)}>
      <SwipeScroll ref={scrollContainerRef} className="relative w-full">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${dynamicH}`}
          className="h-auto w-full"
          style={{ overflow: "visible", minWidth: `${W}px` }}
        >
          <SvgFadeMask id={maskId} />
          <g mask={`url(#${maskId})`}>
            {[24, 60, 100, 140].map((y) => (
              <line
                key={y}
                x1={0}
                x2={W}
                y1={y}
                y2={y}
                stroke="var(--grid)"
                strokeDasharray="4 4"
                strokeWidth="1"
                fill="none"
              />
            ))}
          </g>

          <defs>
            <marker
              id={arrowSolidId}
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
              markerUnits="strokeWidth"
            >
              <path d="M 1 1 L 9 5 L 1 9 Z" fill="var(--status-1)" />
            </marker>
            <marker
              id={arrowPendingId}
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
              markerUnits="strokeWidth"
            >
              <path d="M 1 1 L 9 5 L 1 9 Z" fill="var(--status-neutral)" fillOpacity="0.7" />
            </marker>
            {free &&
              resolved.categories.map((cat) => (
                <marker
                  key={cat.id}
                  id={`${arrowPrefix}-${categorySvgKey(cat.id)}`}
                  viewBox="0 0 10 10"
                  refX="9"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                  markerUnits="strokeWidth"
                >
                  <path d="M 1 1 L 9 5 L 1 9 Z" fill={cat.color} />
                </marker>
              ))}
          </defs>

          {Array.from({ length: Math.max(0, steps.length - 1) }).map((_, i) => {
            const isPending = grouped && !free && i >= progressEnd;
            const x1 = xFor(i) + segInset;
            const x2 = xFor(i + 1) - segInset;
            if (x2 <= x1) return null;
            // A segment belongs to the step it leaves, so a legend pick dims it
            // together with that step's group, the way the edges of the
            // fork-join and state-machine modules do.
            const segKey = stepKey(steps[i]);
            const segDim = legFilter !== null && legFilter !== segKey;
            const segTransition = { transition: "opacity 0.15s" };
            if (free) {
              const known =
                segKey !== undefined &&
                resolved.categories.some((c) => c.id === segKey);
              const svgKey = known ? categorySvgKey(segKey) : null;
              return (
                <line
                  key={`seg-${i}`}
                  x1={x1}
                  y1={yLine}
                  x2={x2}
                  y2={yLine}
                  stroke={resolveCategoryColor(
                    segKey,
                    resolved.categories,
                    "var(--chart-1)",
                  )}
                  strokeWidth="2"
                  // Butt, not round: a round cap pokes half a stroke width past
                  // the arrow tip and blunts it, and the head covers the flat
                  // end anyway.
                  strokeLinecap="butt"
                  opacity={segDim ? 0.35 : 1}
                  style={segTransition}
                  markerEnd={
                    svgKey
                      ? `url(#${arrowPrefix}-${svgKey})`
                      : `url(#${arrowSolidId})`
                  }
                />
              );
            }
            if (isPending) {
              return (
                <line
                  key={`seg-${i}`}
                  x1={x1}
                  y1={yLine}
                  x2={x2}
                  y2={yLine}
                  stroke="var(--status-neutral)"
                  strokeWidth="1.5"
                  strokeDasharray="2 5"
                  strokeLinecap="round"
                  opacity={segDim ? 0.25 : 0.7}
                  style={segTransition}
                  markerEnd={`url(#${arrowPendingId})`}
                />
              );
            }
            return (
              <line
                key={`seg-${i}`}
                x1={x1}
                y1={yLine}
                x2={x2}
                y2={yLine}
                stroke="var(--status-1)"
                strokeWidth="2"
                strokeLinecap="butt"
                opacity={segDim ? 0.35 : 1}
                style={segTransition}
                markerEnd={`url(#${arrowSolidId})`}
              />
            );
          })}

          {steps.map((s, i) => (
            <g
              key={`v-${s.id}`}
              opacity={
                engaged !== null && engaged !== i && selected !== i ? 0.35 : 1
              }
              style={{ transition: "opacity 0.15s" }}
            >
              <line
                x1={xFor(i)}
                y1={yTopLbl + 8}
                x2={xFor(i)}
                y2={yLine - 14}
                stroke="var(--grid)"
                strokeDasharray="4 4"
                strokeWidth="1"
                fill="none"
              />
              <line
                x1={xFor(i)}
                y1={yLine + 14}
                x2={xFor(i)}
                y2={yBotLbl - 10}
                stroke="var(--grid)"
                strokeDasharray="4 4"
                strokeWidth="1"
                fill="none"
              />
            </g>
          ))}

          {steps.map((s, i) => {
            const labelW = Math.max(48, step - 8);
            return (
              <foreignObject
                key={`n-${s.id}`}
                x={xFor(i) - labelW / 2}
                y={yTopLbl - 24}
                width={labelW}
                height={40}
                overflow="visible"
              >
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    height: "100%",
                    color:
                      i === active ? "var(--foreground)" : "var(--muted-foreground)",
                    fontSize: "12px",
                    fontWeight: i === active ? 600 : 500,
                    lineHeight: 1.15,
                    textAlign: "center",
                    overflowWrap: "anywhere",
                    transition: "color 0.15s",
                  }}
                >
                  {stripInlineMarkdown(s.name)}
                </div>
              </foreignObject>
            );
          })}

          {steps.map((s, i) => {
            const labelW = Math.max(48, step - 8);
            return (
              <foreignObject
                key={`d-${s.id}`}
                x={xFor(i) - labelW / 2}
                y={yBotLbl - 6}
                width={labelW}
                height={32}
                overflow="visible"
              >
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    color: i === active ? "var(--muted-foreground)" : "var(--subtle)",
                    fontSize: "11px",
                    lineHeight: 1.15,
                    textAlign: "center",
                    overflowWrap: "anywhere",
                    transition: "color 0.15s",
                  }}
                >
                  {s.date}
                </div>
              </foreignObject>
            );
          })}

          {steps.map((s, i) => {
            const cx = xFor(i);
            const isDone = !free && s.status === "done";
            const isActive = !free && s.status === "active";
            const isPending = !free && s.status === "pending";
            const isSelected = i === selected;
            const isHovered = i === hovered;
            const nodeColor = resolveCategoryColor(
              stepKey(s),
              resolved.categories,
              "var(--chart-1)",
            );
            const legDim = legFilter !== null && legFilter !== stepKey(s);

            return (
              <g
                key={`m-${s.id}`}
                ref={(el) => {
                  anchorRefs.current[i] = el;
                }}
                data-module-type="timeline-stepper"
                data-part-index={i}
                data-part-label={s.name}
                onPointerEnter={(e) => {
                  if (hoverGesture.enter(e)) marker.setHovered(i);
                }}
                onPointerLeave={(e) => {
                  if (hoverGesture.leave(e)) marker.setHovered(null);
                }}
                onClick={(e) =>
                  marker.select(i, {
                    kind: "step",
                    index: i,
                    element: e.currentTarget,
                  })
                }
                style={{
                  cursor: "pointer",
                  opacity: legDim ? 0.35 : 1,
                  transition: "opacity 0.15s",
                }}
              >
                <circle cx={cx} cy={yLine} r={18} fill="transparent" />
                {(isSelected || isHovered) && !isActive && (
                  <circle
                    cx={cx}
                    cy={yLine}
                    r={11}
                    fill="none"
                    stroke={nodeColor}
                    strokeWidth="1.5"
                    opacity="0.45"
                  />
                )}
                {free && <circle cx={cx} cy={yLine} r={dotR} fill={nodeColor} />}
                {!free && !grouped && (
                  <circle cx={cx} cy={yLine} r={dotR} fill="var(--status-1)" />
                )}
                {grouped && (isDone || isActive) && (
                  <circle cx={cx} cy={yLine} r={dotR} fill={nodeColor} />
                )}
                {grouped && isPending && (
                  <circle
                    cx={cx}
                    cy={yLine}
                    r={4}
                    fill="var(--background)"
                    stroke="var(--status-neutral)"
                    strokeWidth="1.5"
                  />
                )}
              </g>
            );
          })}
        </svg>
      </SwipeScroll>

      <NodeTooltip
        visible={activeStep !== null}
        anchor={engaged !== null ? anchorRefs.current[engaged] ?? null : null}
        contentKey={activeStep ? String(activeStep.id) : undefined}
        title={stripInlineMarkdown(activeStep?.name) || undefined}
        rows={
          activeStep
            ? [
                { color: "var(--muted-foreground)", label: t("Date"), value: activeStep.date },
                ...(activeStep.duration
                  ? [{ color: "var(--muted-foreground)", label: t("Duration"), value: activeStep.duration }]
                  : []),
                ...(activeStatusMeta
                  ? [{ color: activeStatusMeta.color, label: t("Status"), value: activeStatusMeta.label }]
                  : []),
              ]
            : []
        }
      />

      {showLegend && grouped && (
        <SwipeScroll>
          <Legend
            items={legendItems}
            hoveredIndex={legend.hovered}
            onHoverChange={legend.setHovered}
            selectedIndex={legendSelectedIndex}
            onSelect={legend.select}
            onLabelRef={legend.setLabelRef}
            className="flex flex-row gap-1"
          >
            <LegendItem className="flex shrink-0 items-center gap-2 px-2 py-1">
              <LegendMarker />
              <LegendLabel />
              <LegendValue />
            </LegendItem>
          </Legend>
        </SwipeScroll>
      )}
    </div>
  );
}
